import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  type Folder,
  type Task,
  type Invite,
  type InviteComponent,
  type CalStatus,
} from '@zimbra-webif/sdk';
import { useClient } from './ClientContext';

/* ========================================================================= */
/* Search-hit shape                                                          */
/* ========================================================================= */

/**
 * A `<task>` hit from `Search` (`types=task`). This is the flat
 * `LegacyCalendaringData`-style shape (NOT the nested GetTask `Task`): the
 * task-specific fields live directly on the hit, with the due date carried on
 * the expanded `<inst dueDate>` instances.
 *
 * Fields below are sourced from docs/features/calendar-tasks.md feature 17a
 * ("id, fba, transp, status, ptst, allDay, otherAtt, alarm, recur, hasEx,
 * name, loc, invId, compNum, isOrg, priority, percentComplete[task], dur ...
 * with expanded <inst s tzo [dueDate tzoDue(task)] ...>"). Anything the server
 * sends beyond these is preserved by the open index signature.
 */
export interface TaskSearchInstance {
  /** start (ms epoch) */
  s?: number;
  /** VTODO due date (ms epoch) */
  dueDate?: number;
  /** due timezone offset (ms) */
  tzoDue?: number;
  ridZ?: string;
  [k: string]: unknown;
}

export interface TaskHit {
  /** task item id */
  id?: string;
  /** invite id (for GetTask / Modify follow-ups) */
  invId?: number | string;
  /** comp number */
  compNum?: number;
  /** iCalendar UID */
  uid?: string;
  /** VTODO status NEED|INPR|COMP|WAITING|DEFERRED */
  status?: CalStatus;
  /** summary / subject */
  name?: string;
  /** location */
  loc?: string;
  /** priority 0-9 */
  priority?: number;
  /** percent complete 0-100 */
  percentComplete?: number;
  /** folder id */
  l?: string;
  /** flags */
  f?: string;
  /** expanded instances (carry dueDate) */
  inst?: TaskSearchInstance[];
  [k: string]: unknown;
}

/** The due date (ms epoch) for a hit, taken from the first expanded instance. */
export function dueOf(t: TaskHit): number | undefined {
  return t.inst?.find((i) => typeof i.dueDate === 'number')?.dueDate;
}

/* ========================================================================= */
/* Task-list folders (folders whose view === 'task', incl. shared links)     */
/* ========================================================================= */

export interface TaskFolderRow {
  id: string;
  name: string;
  depth: number;
  shared: boolean;
}

function collectTaskFolders(root: Folder | undefined): TaskFolderRow[] {
  const rows: TaskFolderRow[] = [];
  const walk = (folders: Folder[] | undefined, depth: number, sharedParent: boolean) => {
    for (const f of folders ?? []) {
      const shared = sharedParent || 'owner' in f || 'zid' in f;
      if (f.view === 'task') {
        rows.push({ id: f.id, name: f.name ?? '', depth, shared });
      }
      // descend into both sub-folders and shared mountpoints (`link`)
      walk(f.folder, depth + 1, shared);
      walk(f.link, depth + 1, true);
    }
  };
  walk(root?.folder, 0, false);
  walk(root?.link, 0, true);
  return rows;
}

/** All task-view folders (local + shared), flattened for the sidebar. */
export function useTaskFolders() {
  const { client } = useClient();
  return useQuery({
    queryKey: ['task-folders'],
    queryFn: async (): Promise<TaskFolderRow[]> => {
      const res = await client.mail.getFolderTree();
      return collectTaskFolders(res.folder?.[0]);
    },
  });
}

/* ========================================================================= */
/* Task list (Search, types=task)                                           */
/* ========================================================================= */

const TASK_SORTS: Record<string, string> = {
  taskDueAsc: 'taskDueAsc',
  taskDueDesc: 'taskDueDesc',
  taskStatusAsc: 'taskStatusAsc',
  taskPercCompletedAsc: 'taskPercCompletedAsc',
};

/**
 * Tasks in a given task folder (by id), or across all task folders when
 * `folderId` is null. Uses the mail `Search` command with `types=task` — the
 * documented task-list path (feature 17a). The result is cast to read the
 * `task` hit array off the JsonObject response.
 */
export function useTasks(folderId: string | null, sortBy: string) {
  const { client } = useClient();
  const sort = TASK_SORTS[sortBy] ?? 'taskDueAsc';
  return useQuery({
    queryKey: ['tasks', folderId, sort],
    queryFn: async (): Promise<TaskHit[]> => {
      const query = folderId ? `inid:${folderId}` : 'in:"Tasks"';
      const res = await client.mail.search({
        query,
        types: 'task',
        sortBy: sort,
        limit: 200,
      });
      return ((res as { task?: TaskHit[] }).task ?? []) as TaskHit[];
    },
  });
}

/* ========================================================================= */
/* Task detail (GetTask)                                                     */
/* ========================================================================= */

/** Pull the first/default component off a fetched task. */
export function defaultComp(task: Task | undefined): InviteComponent | undefined {
  const inv: Invite | undefined = task?.inv?.[0];
  return inv?.comp?.[0];
}

/** Full task (all invites + replies) via GetTask. */
export function useTask(id: string | null) {
  const { client } = useClient();
  return useQuery({
    queryKey: ['task', id],
    enabled: id !== null,
    queryFn: async (): Promise<Task | undefined> => {
      const res = await client.tasks.getTask({ id: id as string });
      const t = res.task;
      return Array.isArray(t) ? t[0] : t;
    },
  });
}

/* ========================================================================= */
/* Mutations                                                                 */
/* ========================================================================= */

function invalidateTasks(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: ['tasks'] });
  void qc.invalidateQueries({ queryKey: ['task'] });
}

/**
 * Toggle a (non-recurring) task between NEED and COMP via ModifyTask.
 *
 * ModifyTask requires the fetched version's `ms`/`rev` and the default invite
 * id/comp. We GetTask first to read those, then resend the existing `<comp>`
 * with status/percentComplete swapped. This is the whole-task complete toggle;
 * single recurring-instance completion uses CompleteTaskInstance (not wired
 * here — the list checkbox completes the whole task).
 */
export function useToggleComplete() {
  const { client } = useClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, complete }: { id: string; complete: boolean }) => {
      const res = await client.tasks.getTask({ id, sync: true });
      const task = Array.isArray(res.task) ? res.task[0] : res.task;
      const inv: Invite | undefined = task?.inv?.[0];
      const comp: InviteComponent | undefined = inv?.comp?.[0];
      if (!task || !inv || !comp || inv.id === undefined) {
        throw new Error('Task has no editable invite');
      }
      const ms = typeof task.ms === 'number' ? task.ms : 0;
      const rev = typeof task.rev === 'number' ? task.rev : 0;

      const nextComp: InviteComponent = {
        ...comp,
        status: complete ? 'COMP' : 'NEED',
        percentComplete: complete ? 100 : 0,
      };
      if (complete) {
        nextComp.completed = toCompletedStamp(new Date());
      } else {
        delete nextComp.completed;
      }

      return client.tasks.modifyTask({
        id: inv.id,
        comp: typeof comp.compNum === 'number' ? comp.compNum : 0,
        ms,
        rev,
        message: {
          su: comp.name,
          inv: { tz: inv.tz, comp: [nextComp] },
        },
      });
    },
    onSuccess: () => invalidateTasks(qc),
  });
}

export interface NewTaskInput {
  name: string;
  folderId?: string;
  priority?: number;
  /** due date (ms epoch), all-day */
  due?: number;
  notes?: string;
}

/**
 * Create a simple VTODO via CreateTask. We build a minimal `<inv><comp>` with
 * status NEED and (optionally) an all-day due date carried on `<e>`. The
 * date-only `d` form ("YYYYMMDD") marks the due as all-day per the DATETIME
 * wire convention.
 */
export function useCreateTask() {
  const { client } = useClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: NewTaskInput) => {
      const comp: InviteComponent = {
        status: 'NEED',
        percentComplete: 0,
        name: input.name,
      };
      if (input.priority !== undefined) comp.priority = input.priority;
      if (input.due !== undefined) comp.e = { d: toDateOnly(new Date(input.due)) };
      if (input.notes) comp.desc = input.notes;

      return client.tasks.createTask({
        message: {
          su: input.name,
          inv: { comp: [comp] },
          ...(input.folderId ? { l: input.folderId } : {}),
        },
      });
    },
    onSuccess: () => invalidateTasks(qc),
  });
}

/* ========================================================================= */
/* Datetime helpers (wire strings)                                           */
/* ========================================================================= */

function pad(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

/** "YYYYMMDD" (local) — date-only DATETIME = all-day. */
export function toDateOnly(d: Date): string {
  return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}`;
}

/** "YYYYMMDDThhmmssZ" (UTC) — VTODO COMPLETED stamp. */
function toCompletedStamp(d: Date): string {
  return (
    `${d.getUTCFullYear()}${pad(d.getUTCMonth() + 1)}${pad(d.getUTCDate())}` +
    `T${pad(d.getUTCHours())}${pad(d.getUTCMinutes())}${pad(d.getUTCSeconds())}Z`
  );
}
